import { useState } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import { Checkbox } from "./ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { ROLE_LABEL, ALL_ROLES, EVENT_COLORS, getCategories } from "../lib/permissions";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Tags } from "lucide-react";

const EMPTY = {
  name: "", direct_roles: [], approval_roles: [], approvers: [],
  hidden_roles: [], anyone: false, prompts: { forms: true, location: true, times: true },
};

function RoleChecks({ label, values, onChange, testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-neutral-400">{label}</Label>
      <div className="flex flex-wrap gap-1.5" data-testid={testid}>
        {ALL_ROLES.map(r => (
          <label key={r} data-testid={`${testid}-${r}`}
            className={`flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-full border cursor-pointer transition ${
              values.includes(r)
                ? "bg-[var(--sn-gold)]/15 text-[var(--sn-gold)] border-[var(--sn-gold)]/50"
                : "bg-neutral-900 text-neutral-500 border-neutral-700 hover:border-neutral-500"
            }`}>
            <Checkbox checked={values.includes(r)}
              onCheckedChange={(c) => onChange(c ? [...values, r] : values.filter(x => x !== r))}
              className="h-3 w-3 border-neutral-600 data-[state=checked]:bg-[var(--sn-gold)] data-[state=checked]:border-[var(--sn-gold)]" />
            {ROLE_LABEL[r]}
          </label>
        ))}
      </div>
    </div>
  );
}

export default function CategoriesView({ onRefresh }) {
  const cats = getCategories();
  const [editing, setEditing] = useState(null); // null | { ...form, isNew, builtin }
  const [saving, setSaving] = useState(false);

  const openEdit = (c) => setEditing({
    isNew: false, builtin: !!c.builtin, name: c.name,
    direct_roles: c.direct_roles || [], approval_roles: c.approval_roles || [],
    approvers: c.approvers || [], hidden_roles: c.hidden_roles || [],
    anyone: !!c.anyone, prompts: { forms: true, location: true, times: true, ...(c.prompts || {}) },
  });

  const save = async () => {
    if (!editing.name.trim()) { toast.error("Category name required"); return; }
    setSaving(true);
    try {
      const payload = {
        name: editing.name.trim(), direct_roles: editing.direct_roles,
        approval_roles: editing.approval_roles, approvers: editing.approvers,
        hidden_roles: editing.hidden_roles, anyone: editing.anyone, prompts: editing.prompts,
      };
      if (editing.isNew) await api.post("/categories", payload);
      else await api.put(`/categories/${encodeURIComponent(editing.name)}`, payload);
      toast.success("Category saved — live for everyone");
      setEditing(null);
      onRefresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (name) => {
    if (!window.confirm(`Delete category "${name}"?`)) return;
    try {
      await api.delete(`/categories/${encodeURIComponent(name)}`);
      toast.success("Category deleted");
      onRefresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
  };

  return (
    <div className="space-y-6" data-testid="categories-view">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl text-[var(--sn-gold)] flex items-center gap-2">
            <Tags className="w-6 h-6" /> Event Categories
          </h2>
          <p className="text-neutral-500 text-sm">Define who creates, who approves, who sees, and which prompts appear. Changes apply live.</p>
        </div>
        <Button data-testid="new-category-button" onClick={() => setEditing({ ...EMPTY, isNew: true, builtin: false })}
          className="bg-[var(--sn-gold)] hover:brightness-95 text-black font-bold gap-2">
          <Plus className="w-4 h-4" /> New Category
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {cats.map(c => {
          const color = EVENT_COLORS[c.name];
          return (
            <Card key={c.name} className="glass-card p-4 space-y-2" data-testid={`category-card-${c.name}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: color?.dot }} />
                  <span className="font-heading text-neutral-100">{c.name}</span>
                  {!c.builtin && <Badge className="bg-purple-950 text-purple-300 border-purple-700 border text-[9px]">custom</Badge>}
                </div>
                <div className="flex gap-1">
                  <Button data-testid={`edit-category-${c.name}`} variant="ghost" size="icon"
                    onClick={() => openEdit(c)} className="text-neutral-400 hover:text-[var(--sn-gold)] h-8 w-8">
                    <Pencil className="w-4 h-4" />
                  </Button>
                  {!c.builtin && (
                    <Button data-testid={`delete-category-${c.name}`} variant="ghost" size="icon"
                      onClick={() => remove(c.name)} className="text-neutral-500 hover:text-rose-400 h-8 w-8">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
              <div className="text-[11px] space-y-1 text-neutral-400">
                <div><span className="text-neutral-500">Create directly:</span> {(c.direct_roles || []).map(r => ROLE_LABEL[r]).join(", ") || "—"}{c.anyone ? " · anyone (with approval)" : ""}</div>
                <div><span className="text-neutral-500">Needs approval:</span> {(c.approval_roles || []).map(r => ROLE_LABEL[r]).join(", ") || "—"}</div>
                <div><span className="text-neutral-500">Approved by:</span> {(c.approvers || []).map(r => ROLE_LABEL[r]).join(", ") || "—"}</div>
                {(c.hidden_roles || []).length > 0 && (
                  <div><span className="text-neutral-500">Hidden from:</span> {c.hidden_roles.map(r => ROLE_LABEL[r]).join(", ")}</div>
                )}
                <div><span className="text-neutral-500">Prompts:</span> {["forms", "location", "times"].filter(k => c.prompts?.[k]).map(k => k === "forms" ? "Required forms" : k === "location" ? "Location" : "Start/End time").join(" · ") || "none"}</div>
              </div>
            </Card>
          );
        })}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#141414] border-[var(--sn-border)] w-[calc(100vw-1.5rem)] sm:max-w-lg max-h-[92vh] overflow-y-auto" data-testid="category-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[var(--sn-gold)]">
              {editing?.isNew ? "New Category" : `Edit: ${editing?.name}`}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Category name *</Label>
                <Input data-testid="category-name-input" value={editing.name} disabled={!editing.isNew}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  placeholder="e.g. Date Dash" className="bg-neutral-900 border-white/10" />
              </div>
              <RoleChecks label="Can create WITHOUT approval (plus President/VP/Admin always can)" values={editing.direct_roles}
                onChange={(v) => setEditing({ ...editing, direct_roles: v })} testid="cat-direct" />
              <RoleChecks label="Can create WITH approval from the responsible chair" values={editing.approval_roles}
                onChange={(v) => setEditing({ ...editing, approval_roles: v })} testid="cat-approval" />
              <RoleChecks label="Approves pending events (responsible chair)" values={editing.approvers}
                onChange={(v) => setEditing({ ...editing, approvers: v })} testid="cat-approvers" />
              <RoleChecks label="Hidden from (everyone else can view)" values={editing.hidden_roles}
                onChange={(v) => setEditing({ ...editing, hidden_roles: v })} testid="cat-hidden" />
              <div className="flex items-center justify-between rounded-lg border border-white/10 p-3 bg-black/30">
                <div>
                  <Label className="text-neutral-200">Any brother can request (with approval)</Label>
                  <p className="text-xs text-neutral-500">e.g. Candidate Events — anyone schedules, chair approves</p>
                </div>
                <Switch data-testid="cat-anyone" checked={editing.anyone}
                  onCheckedChange={(v) => setEditing({ ...editing, anyone: v })} />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-neutral-400">Prompts shown when creating this event</Label>
                {[["forms", "Required forms (description + attachment)"], ["location", "Location / address"], ["times", "Start & end time (off = all-day)"]].map(([k, label]) => (
                  <div key={k} className="flex items-center justify-between rounded-lg border border-white/10 p-3 bg-black/30">
                    <span className="text-sm text-neutral-200">{label}</span>
                    <Switch data-testid={`cat-prompt-${k}`} checked={!!editing.prompts[k]}
                      onCheckedChange={(v) => setEditing({ ...editing, prompts: { ...editing.prompts, [k]: v } })} />
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="border-neutral-700 text-neutral-300">Cancel</Button>
            <Button data-testid="category-save-button" onClick={save} disabled={saving}
              className="bg-[var(--sn-gold)] text-black hover:brightness-95 font-bold">
              {saving ? "Saving…" : "Save Category"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
